import React, {useState, useEffect} from "react";
import {Navigate, useNavigate} from "react-router-dom";
import {AppRouteUrl, TextType} from "@pinch-financial/pinch-ui-components";
import {useDispatch, useSelector} from "react-redux";
import {getProfileFromRecovery} from "store/selectors/auth";
import {initialize, submit} from "./SetPasswordPage.action";
import {
  getAllOperationFailures,
  getIsAnyOperationPending,
  getIsOperationFailure,
  getIsOperationNotStarted,
  getOperationFailure,
  getOperationResult,
} from "store/selectors/operation";
import {OperationType} from "types/operation";
import {HttpStatusCode} from "axios";
import {useForm} from "react-hook-form";
import {getSchema} from "./SetPasswordPage.schema";
import {yupResolver} from "@hookform/resolvers/yup";
import {
  SetPasswordPage as SetPasswordPageUI,
  SetPasswordPagePayload,
} from "@pinch-financial/pinch-ui-components";
import NavigationBar from "components/NavigationBar/NavigationBar";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import {getSearchParamsOf} from "components/utils/urlUtil";
import {useTranslation} from "react-i18next";
import usePageText from "~/hooks/usePageText";

interface Props {
  containerRef?: React.RefObject<HTMLDivElement>;
}

const SetPasswordPage = ({containerRef}: Props) => {
  const {t: fixedT} = useTranslation("", {keyPrefix: "setPasswordPage"});
  const {getText, status: textStatus, isTipBoxEnabled} = usePageText("ACTIVATE_ACCOUNT_URL");
  const dispatch = useDispatch();

  const [inputError, setInputError] = useState<string>("");
  const [shouldLoadPageText, setShouldLoadPageText] = useState(false);
  const isActivationNotStarted = useSelector(getIsOperationNotStarted)(
    OperationType.verifyRecoveryToken
  );
  const profileFromActivation = useSelector(getProfileFromRecovery);
  const failures = useSelector(getAllOperationFailures)(
    OperationType.verifyRecoveryToken,
    OperationType.setPassword
  );
  const isAnyPending = useSelector(getIsAnyOperationPending)(
    OperationType.verifyRecoveryToken,
    OperationType.setPassword
  );
  const {isDone: fetchFinancialInstDone} = useSelector(getOperationResult)(
    OperationType.fetchAppAssociatedFinancialInstitutionByAppOrAuthId
  );

  const isKeyboardOpen = useDetectKeyboardOpen();

  const isActivationFailure = useSelector(getIsOperationFailure)(OperationType.verifyRecoveryToken);
  const isSetPasswordOperationFailure = useSelector(getOperationFailure)(OperationType.setPassword);

  const navigate = useNavigate();

  const formHook = useForm<SetPasswordPagePayload>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(getSchema()),
  });

  const onSubmit = (values: SetPasswordPagePayload) => {
    dispatch(submit({...values, navigate}));
  };

  useEffect(() => {
    if (isSetPasswordOperationFailure) {
      if (isSetPasswordOperationFailure.status === HttpStatusCode.Conflict) {
        setInputError(fixedT("passwordReused")!);
      } else {
        setInputError(fixedT("passwordInvalid")!);
      }
    } else {
      setInputError(failures?.[0] ? fixedT(`messages.backendErrors.${failures[0].type}`)! : "");
    }
  }, [isSetPasswordOperationFailure, failures]);

  useEffect(() => {
    if (fetchFinancialInstDone) {
      setShouldLoadPageText(true);
    }
  }, [fetchFinancialInstDone]);
  const isTextLoading =
    !shouldLoadPageText || textStatus === "not-started" || textStatus === "pending";
  useEffect(() => {
    if (isActivationNotStarted) {
      const {token, appId} = getSearchParamsOf("token", "appId");
      dispatch(initialize({token, appId}));
    }
  }, [isActivationNotStarted]);

  if (isActivationFailure) {
    return <Navigate to={AppRouteUrl.RESEND_EMAIL_URL} />;
  }

  // Keep this so UI can update. React-hook-form problem.
  const {
    formState: {errors, touchedFields: touched},
  } = formHook;

  return (
    <SetPasswordPageUI
      loading={isTextLoading}
      isMobileKeyboardOpen={isKeyboardOpen}
      containerRef={containerRef}
      header={getText(TextType.APP_PAGE_HEADER) ?? fixedT("header")!}
      description={getText(TextType.APP_PAGE_BODY) ?? fixedT("description")!}
      tipHeader={
        isTipBoxEnabled ? getText(TextType.APP_PAGE_TIP_HEADER) ?? fixedT("tipHeader")! : ""
      }
      tipBody={
        isTipBoxEnabled && (
          <span
            dangerouslySetInnerHTML={{
              __html: getText(TextType.APP_PAGE_TIP_BODY) ?? fixedT("tipBody")!,
            }}
          />
        )
      }
      navigationBar={<NavigationBar />}
      onNextPage={onSubmit}
      nextButtonText={getText(TextType.APP_PAGE_NEXT_BUTTON) ?? fixedT("forwardButton")}
      inputError={inputError}
      isPending={isAnyPending}
      isFetchFIDone={fetchFinancialInstDone}
      passwordLabel={fixedT("password")}
      passwordPlaceholder={fixedT("password")}
      confirmPasswordLabel={fixedT("confirmPassword")}
      confirmPasswordPlaceholder={fixedT("confirmPassword")}
      invalidPassword={fixedT("passwordInvalid")}
      mismatchPassword={fixedT("passwordMismatch")}
      profileFromActivationFirstName={profileFromActivation?.firstName || ""}
      profileFromActivationLastName={profileFromActivation?.lastName || ""}
      profileFromActivationEmail={profileFromActivation?.login || ""}
    />
  );
};

export default SetPasswordPage;
